import { assignColorToStudents } from '@/components/shared/StudentAvatar/utils/assignColorToStudents';
import type { Id } from '@/types/api';
import type { Center } from '@/types/center';
import type { Guardian } from '@/types/guardian';
import type { Student } from '@/types/student';
import type { AuthUser } from 'aws-amplify/auth';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

type AuthStatus = 'IDLE' | 'LOADING' | 'AUTHENTICATED' | 'UNAUTHENTICATED';

interface UserData {
  cognitoUser: AuthUser;
  guardian: Guardian;
  students: Student[];
  center: Center;
  creditCardExpiriesAt?: Date;
  failedPayment: boolean;
  overdueInvoice: boolean;
  isDemoEligible: boolean;
  ketchToken?: string;
}

interface AuthState {
  userData: UserData | null;
  studentsBgColors: Map<Id, string>;

  status: AuthStatus;
  setStatus: (status: AuthStatus) => void;

  login: (user: UserData) => void;

  updateUserData: (data: Partial<UserData>) => void;

  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  devtools((set, get) => ({
    status: 'IDLE',
    setStatus: (status) => set({ status }),

    userData: null,
    studentsBgColors: {},

    login: (userData) => {
      const bgColors = assignColorToStudents(userData?.students ?? []);
      set({ userData, status: 'AUTHENTICATED', studentsBgColors: bgColors });
    },

    updateUserData: (data) => {
      const { userData } = get();

      if (!userData) {
        return;
      }

      const updatedUserData = { ...userData, ...data };
      const bgColors = assignColorToStudents(updatedUserData.students);

      set({ userData: updatedUserData, studentsBgColors: bgColors });
    },

    logout: () => {
      set({ userData: null, status: 'UNAUTHENTICATED', studentsBgColors: new Map() });
    },
  })),
);
